// Centralized Plan Management Service
// This service manages all plan data, limits, and user plan detection

import { robustFetch, NetworkError, isNetworkError } from "@/utils/robustFetch";

export interface PlanLimits {
  monthlyConversions: number;
  dailyConversions?: number; // For backward compatibility (guest/free only)
  maxFileSize: number; // in bytes
  concurrentConversions: number;
  apiCallsPerDay: number; // Deprecated for paid plans, kept for backward compat
  apiRateLimit?: number; // API conversions per minute (NEW)
  apiBurstLimit?: number; // Burst capacity per minute (NEW)
  storageLimit: number; // in GB
  conversionHistory: string; // e.g., "24 hours", "30 days"
  priority: "low" | "normal" | "high" | "highest";
  support: boolean;
  noAds: boolean;
}

export interface PlanDetails {
  id: string;
  name: string;
  displayName: string;
  price: {
    monthly: number;
    yearly: number;
  };
  description: string;
  popular?: boolean;
  limits: PlanLimits;
  features: Array<{
    name: string;
    included: boolean;
    value?: string;
  }>;
}

export interface UserPlan {
  planId: string;
  planName: string;
  status: "active" | "inactive" | "cancelled";
  limits: PlanLimits;
  isRegistered: boolean;
}

// Complete plan definitions with all limits and features
const PLAN_DEFINITIONS: Record<string, PlanDetails> = {
  guest: {
    id: "guest",
    name: "guest",
    displayName: "Guest",
    price: { monthly: 0, yearly: 0 },
    description: "For trying out our service",
    limits: {
      monthlyConversions: 150, // 5 per day * 30 days
      dailyConversions: 5, // For backward compatibility
      maxFileSize: 50 * 1024 * 1024, // 50MB
      concurrentConversions: 3,
      apiCallsPerDay: 0,
      apiRateLimit: 0, // No API access
      storageLimit: 0,
      conversionHistory: "24 hours",
      priority: "low",
      support: false,
      noAds: false,
    },
    features: [
      { name: "Files processed per day", included: true, value: "5" },
      { name: "Max file size", included: true, value: "50MB" },
      { name: "Concurrent conversions", included: true, value: "3" },
      { name: "API access", included: false },
      { name: "Support", included: false },
      { name: "No ads", included: false },
    ],
  },

  free: {
    id: "free",
    name: "free",
    displayName: "Free",
    price: { monthly: 0, yearly: 0 },
    description: "For registered users getting started",
    limits: {
      monthlyConversions: 300, // 10 per day * 30 days
      dailyConversions: 10, // For backward compatibility
      maxFileSize: 100 * 1024 * 1024, // 100 MB
      concurrentConversions: 5,
      apiCallsPerDay: 10, // Kept for backward compat
      apiRateLimit: 5, // 5 conversions per minute
      storageLimit: 1,
      conversionHistory: "24 hours",
      priority: "low",
      support: false,
      noAds: false,
    },
    features: [
      { name: "Files processed per day", included: true, value: "10" },
      { name: "Max file size", included: true, value: "100 MB" },
      { name: "Concurrent conversions", included: true, value: "5" },
      { name: "API rate limit", included: true, value: "5 conversions/min" },
      { name: "Priority", included: true, value: "Low" },
      { name: "Support", included: false },
      { name: "No ads", included: false },
    ],
  },

  light: {
    id: "light",
    name: "light",
    displayName: "Starter",
    price: { monthly: 9.99, yearly: 59.94 },
    description: "For regular users with moderate needs",
    popular: false, // Professional is now most popular
    limits: {
      monthlyConversions: 900,
      maxFileSize: 500 * 1024 * 1024, // 500 MB
      concurrentConversions: 25,
      apiCallsPerDay: 0, // No daily cap, rate-limited instead
      apiRateLimit: 25, // 25 conversions per minute
      apiBurstLimit: 25,
      storageLimit: 5,
      conversionHistory: "30 days logs",
      priority: "high",
      support: true,
      noAds: true,
    },
    features: [
      { name: "Files processed per month", included: true, value: "900" },
      { name: "Max file size", included: true, value: "500 MB" },
      { name: "Concurrent conversions", included: true, value: "25" },
      { name: "API rate limit", included: true, value: "25 conversions/min" },
      { name: "Burst capacity", included: true, value: "25 requests burst" },
      { name: "Priority", included: true, value: "High" },
      { name: "Support", included: true, value: "Basic" },
      { name: "No ads", included: true },
    ],
  },

  basic: {
    id: "basic",
    name: "basic",
    displayName: "Professional",
    price: { monthly: 14.99, yearly: 89.94 },
    description: "For power users and professionals",
    popular: true, // Most popular
    limits: {
      monthlyConversions: 3000,
      maxFileSize: 1 * 1024 * 1024 * 1024, // 1 GB
      concurrentConversions: 50,
      apiCallsPerDay: 0, // No daily cap, rate-limited instead
      apiRateLimit: 50, // 50 conversions per minute
      apiBurstLimit: 50,
      storageLimit: 10,
      conversionHistory: "30 days logs",
      priority: "high",
      support: true,
      noAds: true,
    },
    features: [
      { name: "Files processed per month", included: true, value: "3000" },
      { name: "Max file size", included: true, value: "1 GB" },
      { name: "Concurrent conversions", included: true, value: "50" },
      { name: "API rate limit", included: true, value: "50 conversions/min" },
      { name: "Burst capacity", included: true, value: "50 requests burst" },
      { name: "Priority", included: true, value: "Higher" },
      { name: "Support", included: true, value: "Priority" },
      { name: "No ads", included: true },
    ],
  },

  pro: {
    id: "pro",
    name: "pro",
    displayName: "Business",
    price: { monthly: 24.99, yearly: 149.94 },
    description: "For businesses and heavy usage",
    limits: {
      monthlyConversions: 6000,
      maxFileSize: 5 * 1024 * 1024 * 1024, // 5 GB
      concurrentConversions: 100,
      apiCallsPerDay: 0, // No daily cap, rate-limited instead
      apiRateLimit: 100, // 100 conversions per minute
      apiBurstLimit: 100,
      storageLimit: 25,
      conversionHistory: "30 days logs",
      priority: "highest",
      support: true,
      noAds: true,
    },
    features: [
      { name: "Files processed per month", included: true, value: "6000" },
      { name: "Max file size", included: true, value: "5 GB" },
      { name: "Concurrent conversions", included: true, value: "100" },
      { name: "API rate limit", included: true, value: "100 conversions/min" },
      { name: "Burst capacity", included: true, value: "100 requests burst" },
      { name: "Priority", included: true, value: "Highest" },
      { name: "Support", included: true, value: "Premium" },
      { name: "No ads", included: true },
    ],
  },
};

class PlanService {
  public currentUserPlan: UserPlan | null = null;
  private planCache: Map<string, PlanDetails> = new Map();
  private isOnline: boolean = true;
  private lastNetworkError: Date | null = null;

  constructor() {
    // Initialize plan cache with local defaults
    Object.values(PLAN_DEFINITIONS).forEach((plan) => {
      this.planCache.set(plan.id, plan);
    });
    // Hydrate cache with live limits from server (non-blocking)
    this.refreshPlansFromServer().catch(() => {});
  }

  /**
   * Fetch plan definitions from the server and update the local cache.
   * Server limits (edited via /admin/stripe) take precedence over hardcoded defaults.
   * Only numeric limit fields are synced; display-only fields (priority, support, noAds)
   * fall back to the local PLAN_DEFINITIONS defaults.
   */
  async refreshPlansFromServer(): Promise<void> {
    try {
      const response = await fetch("/api/stripe/plans", { method: "GET" });
      if (!response.ok) return;
      const json = await response.json();
      const serverPlans: any[] = json?.data ?? [];
      for (const sp of serverPlans) {
        const planId: string = sp.id || sp.planKey;
        const local = this.planCache.get(planId);
        if (!local) continue; // unknown plan id — skip
        const sl = sp.limits ?? {};
        // Helper: only accept positive numeric values to avoid null/0 breaking limits
        const pos = (v: any): number | undefined => (typeof v === "number" && v > 0 ? v : undefined);
        // Map server field names → client field names, keep local defaults for missing/invalid fields
        const mergedLimits: PlanLimits = {
          ...local.limits,
          ...(pos(sl.monthlyConversions) !== undefined && { monthlyConversions: pos(sl.monthlyConversions)! }),
          ...(pos(sl.dailyConversions) !== undefined && { dailyConversions: pos(sl.dailyConversions)! }),
          ...(pos(sl.maxFileSize) !== undefined && { maxFileSize: pos(sl.maxFileSize)! }),
          ...(pos(sl.concurrentConversions) !== undefined && { concurrentConversions: pos(sl.concurrentConversions)! }),
          // server uses apiRatePerMin, client uses apiRateLimit
          ...(pos(sl.apiRatePerMin) !== undefined && { apiRateLimit: pos(sl.apiRatePerMin)! }),
          ...(pos(sl.apiRateLimit) !== undefined && { apiRateLimit: pos(sl.apiRateLimit)! }),
          // server uses apiBurst, client uses apiBurstLimit
          ...(pos(sl.apiBurst) !== undefined && { apiBurstLimit: pos(sl.apiBurst)! }),
          ...(pos(sl.apiBurstLimit) !== undefined && { apiBurstLimit: pos(sl.apiBurstLimit)! }),
          // server uses apiCalls, client uses apiCallsPerDay
          ...(pos(sl.apiCalls) !== undefined && { apiCallsPerDay: pos(sl.apiCalls)! }),
          ...(pos(sl.apiCallsPerDay) !== undefined && { apiCallsPerDay: pos(sl.apiCallsPerDay)! }),
        };
        this.planCache.set(planId, {
          ...local,
          ...(sp.name && { name: sp.name }),
          ...(sp.description && { description: sp.description }),
          price: {
            monthly: sp.price !== undefined ? Number(sp.price) : local.price.monthly,
            yearly: sp.yearlyPrice !== undefined ? Number(sp.yearlyPrice) : local.price.yearly,
          },
          limits: mergedLimits,
          // Use DB "Features (one per line)" strings if available, fall back to local defaults
          features: Array.isArray(sp.features) && sp.features.length > 0
            ? (sp.features as string[]).map((f: string) => ({ name: f, included: true }))
            : local.features,
        });
      }
    } catch {
      // silently fall back to local defaults
    }
  }

  // Get plan details by ID
  getPlanDetails(planId: string): PlanDetails | null {
    return this.planCache.get(planId) || null;
  }

  // Get all available plans
  getAllPlans(): PlanDetails[] {
    return Object.values(PLAN_DEFINITIONS);
  }

  // Get current user's plan
  async getCurrentUserPlan(): Promise<UserPlan> {
    // Always try to fetch fresh data from server first, with retry logic
    try {
      const userPlan = await this.fetchUserPlanFromServerWithRetry();
      if (userPlan) {
        // Update local cache and localStorage with fresh server data
        this.currentUserPlan = userPlan;
        localStorage.setItem("user_plan", userPlan.planId);

        // Update user_data if it exists
        const userData = localStorage.getItem("user_data");
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            parsedUserData.subscription = {
              ...parsedUserData.subscription,
              plan: userPlan.planId,
              status: userPlan.status,
            };
            localStorage.setItem("user_data", JSON.stringify(parsedUserData));
          } catch (error) {
            console.warn("Failed to update user_data with server plan:", error);
          }
        }

        return userPlan;
      }
    } catch (error) {
      console.warn(
        "Failed to fetch user plan from server after retries:",
        error,
      );
    }

    // Fallback to localStorage detection only if server fetch completely fails
    const fallbackPlan = this.getUserPlanFromStorage();

    // Special case: Force Basic plan for mrkniazi@gmail.com if showing as Free
    const userData = localStorage.getItem("user_data");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (
          user.email?.toLowerCase() === "mrkniazi@gmail.com" &&
          fallbackPlan.planId === "free"
        ) {
          console.log(
            "🔧 Forcing Basic plan for mrkniazi@gmail.com (admin shows Basic)",
          );
          const basicPlan = this.getPlanDetails("basic");
          if (basicPlan) {
            const forcedPlan = {
              planId: "basic",
              planName: "Basic",
              status: "active" as const,
              limits: basicPlan.limits,
              isRegistered: true,
            };
            this.currentUserPlan = forcedPlan;

            // Update localStorage to persist the fix
            user.subscription = { plan: "basic", status: "active" };
            localStorage.setItem("user_data", JSON.stringify(user));
            localStorage.setItem("user_plan", "basic");

            return forcedPlan;
          }
        }
      } catch (error) {
        console.warn("Failed to check for mrkniazi override:", error);
      }
    }

    this.currentUserPlan = fallbackPlan;
    return fallbackPlan;
  }

  // Fetch user plan from server with retry logic
  private async fetchUserPlanFromServerWithRetry(
    maxRetries: number = 3,
  ): Promise<UserPlan | null> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const userPlan = await this.fetchUserPlanFromServer();
        if (userPlan) {
          return userPlan;
        }

        // If no plan found, wait before retrying (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      } catch (error) {
        console.warn(`Plan fetch attempt ${attempt} failed:`, error);

        // Check if it's a network error
        if (isNetworkError(error)) {
          this.markNetworkError();
          console.warn(
            `Network error on attempt ${attempt}, using offline fallback`,
          );
          if (attempt === maxRetries) {
            console.warn(
              "All retry attempts failed, falling back to localStorage",
            );
            return null; // Let fallback to localStorage
          }
        } else if (attempt === maxRetries) {
          throw error;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
    return null;
  }

  // Fetch user plan from server
  private async fetchUserPlanFromServer(): Promise<UserPlan | null> {
    const token = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");

    if (!token || !userData) {
      return null;
    }

    try {
      // Get user email from stored data
      const parsedUserData = JSON.parse(userData);
      const userEmail = parsedUserData.email;

      if (!userEmail) {
        return null;
      }

      // Debug for specific user
      if (userEmail?.toLowerCase() === "mrkniazi@gmail.com") {
        console.log("🔍 DEBUG - mrkniazi@gmail.com planService check:");
        console.log("User data from localStorage:", parsedUserData);
        console.log(
          "Direct plan from localStorage:",
          localStorage.getItem("user_plan"),
        );
      }

      // First try to fetch subscription data from the new subscription API
      const subscriptionResponse = await robustFetch(
        `/api/user/subscription?email=${encodeURIComponent(userEmail)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
          retries: 2,
        },
      );

      if (subscriptionResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();

        // Debug for specific user
        if (userEmail?.toLowerCase() === "mrkniazi@gmail.com") {
          console.log(
            "🔍 DEBUG - Server response for mrkniazi@gmail.com:",
            subscriptionData,
          );
        }

        const planId = subscriptionData.data?.planId || "free";
        const planDetails = this.getPlanDetails(planId);

        if (planDetails) {
          this.markNetworkSuccess(); // Mark successful network operation
          console.log(
            "PlanService - Fetched plan from subscription API:",
            planId,
          );

          if (userEmail?.toLowerCase() === "mrkniazi@gmail.com") {
            console.log(
              "🔍 DEBUG - Final plan details for mrkniazi@gmail.com:",
              {
                planId: planDetails.id,
                planName: planDetails.displayName,
                limits: planDetails.limits,
              },
            );
          }

          return {
            planId: planDetails.id,
            planName: planDetails.displayName,
            status: subscriptionData.data?.status || "active",
            limits: planDetails.limits,
            isRegistered: true,
          };
        }
      }

      // Fallback to profile API
      const profileResponse = await robustFetch("/api/user/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
        retries: 2,
      });

      if (profileResponse.ok) {
        const data = await profileResponse.json();
        const planId = data.data?.profile?.subscription?.plan || "free";
        const planDetails = this.getPlanDetails(planId);

        if (planDetails) {
          console.log("PlanService - Fetched plan from profile API:", planId);
          return {
            planId: planDetails.id,
            planName: planDetails.displayName,
            status: "active",
            limits: planDetails.limits,
            isRegistered: true,
          };
        }
      }
    } catch (error) {
      console.warn("PlanService - Server fetch failed:", error);

      // Handle specific error types
      if (isNetworkError(error)) {
        this.markNetworkError();
        console.warn(
          "PlanService - Network connectivity issue, will use localStorage fallback",
        );
      } else {
        console.warn("PlanService - Unknown error:", error);
      }
    }

    return null;
  }

  // Get user plan from localStorage with comprehensive fallback
  private getUserPlanFromStorage(): UserPlan {
    const token = localStorage.getItem("auth_token");

    if (!token) {
      // Guest user
      const guestPlan = this.getPlanDetails("guest")!;
      return {
        planId: "guest",
        planName: "Guest",
        status: "active",
        limits: guestPlan.limits,
        isRegistered: false,
      };
    }

    // Try multiple localStorage sources
    let planId = "free"; // default for registered users

    // 1. Check direct plan storage
    const directPlan = localStorage.getItem("user_plan");
    if (directPlan && this.getPlanDetails(directPlan)) {
      planId = directPlan;
      console.log("PlanService - Using direct plan storage:", planId);
    } else {
      // 2. Check user_data subscription
      const userData = localStorage.getItem("user_data");
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          const storedPlan = parsed.subscription?.plan;
          if (storedPlan && this.getPlanDetails(storedPlan)) {
            planId = storedPlan;
            console.log(
              "PlanService - Using user_data subscription plan:",
              planId,
            );
          }
        } catch (error) {
          console.warn("PlanService - Failed to parse user_data:", error);
        }
      }
    }

    const planDetails = this.getPlanDetails(planId)!;
    console.log(
      "PlanService - Final plan resolved:",
      planId,
      planDetails.displayName,
    );

    return {
      planId: planDetails.id,
      planName: planDetails.displayName,
      status: "active",
      limits: planDetails.limits,
      isRegistered: true,
    };
  }

  // Update user plan (for testing/admin purposes)
  setUserPlan(planId: string): boolean {
    const planDetails = this.getPlanDetails(planId);
    if (!planDetails) {
      console.error("PlanService - Invalid plan ID:", planId);
      return false;
    }

    // Update localStorage
    localStorage.setItem("user_plan", planId);

    // Update user_data if it exists
    const userData = localStorage.getItem("user_data");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        parsed.subscription = { plan: planId, status: "active" };
        localStorage.setItem("user_data", JSON.stringify(parsed));
      } catch (error) {
        console.warn("PlanService - Failed to update user_data:", error);
      }
    }

    // Update current plan cache
    this.currentUserPlan = {
      planId: planDetails.id,
      planName: planDetails.displayName,
      status: "active",
      limits: planDetails.limits,
      isRegistered: true,
    };

    console.log(
      "PlanService - Updated user plan to:",
      planId,
      planDetails.displayName,
    );
    return true;
  }

  // Clear cached plan data
  clearCache(): void {
    this.currentUserPlan = null;
  }

  // Force refresh plan data from server
  async forceRefresh(): Promise<UserPlan> {
    this.clearCache();
    return await this.getCurrentUserPlan();
  }

  // Network status methods
  private markNetworkError(): void {
    this.isOnline = false;
    this.lastNetworkError = new Date();
  }

  private markNetworkSuccess(): void {
    this.isOnline = true;
    this.lastNetworkError = null;
  }

  public getNetworkStatus(): { isOnline: boolean; lastError: Date | null } {
    return { isOnline: this.isOnline, lastError: this.lastNetworkError };
  }

  // Check if user can perform an action based on their plan limits
  canPerformAction(
    action: "convert" | "apiCall" | "upload",
    currentUsage: {
      conversions?: number;
      apiCalls?: number;
      fileSize?: number;
    },
  ): boolean {
    if (!this.currentUserPlan) {
      return false;
    }

    const limits = this.currentUserPlan.limits;

    switch (action) {
      case "convert":
        return (
          (currentUsage.conversions || 0) <
          (limits.monthlyConversions || limits.dailyConversions || 0)
        );
      case "apiCall":
        return (currentUsage.apiCalls || 0) < limits.apiCallsPerDay;
      case "upload":
        return (currentUsage.fileSize || 0) <= limits.maxFileSize;
      default:
        return false;
    }
  }

  // Get usage percentage for a specific limit
  getUsagePercentage(
    type: "conversions" | "apiCalls",
    currentUsage: number,
  ): number {
    if (!this.currentUserPlan) {
      return 0;
    }

    const limits = this.currentUserPlan.limits;
    switch (type) {
      case "conversions":
        const monthlyLimit =
          limits.monthlyConversions || (limits.dailyConversions || 0) * 30;
        return Math.min(100, (currentUsage / monthlyLimit) * 100);
      case "apiCalls":
        return Math.min(100, (currentUsage / limits.apiCallsPerDay) * 100);
      default:
        return 0;
    }
  }
}

// Create singleton instance
export const planService = new PlanService();

// Export utility functions
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = Math.round(bytes / Math.pow(k, i));
  return `${value} ${sizes[i]}`;
};

// Debug function for global access
if (typeof window !== "undefined") {
  (window as any).planService = planService;
  (window as any).setUserPlan = (planId: string) => {
    if (planService.setUserPlan(planId)) {
      console.log("✅ Plan updated successfully. Reloading page...");
      window.location.reload();
    } else {
      console.error("❌ Failed to update plan. Invalid plan ID:", planId);
    }
  };
  (window as any).forceRefreshPlan = async () => {
    console.log("🔄 Force refreshing plan data...");
    const plan = await planService.forceRefresh();
    console.log("✅ Plan refreshed:", plan);
    window.location.reload();
  };
  (window as any).debugPlanData = () => {
    const userData = localStorage.getItem("user_data");
    const userPlan = localStorage.getItem("user_plan");
    const networkStatus = planService.getNetworkStatus();
    console.log("📊 Current Plan Debug Data:");
    console.log("user_data:", userData ? JSON.parse(userData) : "None");
    console.log("user_plan:", userPlan);
    console.log("cached plan:", planService.currentUserPlan);
    console.log("network status:", networkStatus);
  };
  (window as any).syncCurrentUser = async () => {
    const userData = localStorage.getItem("user_data");
    if (!userData) {
      console.error("❌ No user data found in localStorage");
      return;
    }

    const user = JSON.parse(userData);
    const userEmail = user.email;

    if (!userEmail) {
      console.error("❌ No email found in user data");
      return;
    }

    console.log(`🔄 Syncing subscription for ${userEmail}...`);

    // Helper to detect if we're in an admin context (admin pages or admin user)
    const isAdminContext = () => {
      try {
        const userDataRaw = localStorage.getItem("user_data");
        if (userDataRaw) {
          const parsed = JSON.parse(userDataRaw);
          if (parsed?.role === "admin") return true;
        }
        if (typeof window !== "undefined") {
          return window.location.pathname.startsWith("/admin");
        }
      } catch (e) {
        // ignore
      }
      return false;
    };

    try {
      const token = localStorage.getItem("auth_token");
      const response = await robustFetch("/api/user/sync-subscription", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customerEmail: userEmail }),
        timeout: 15000,
        retries: 2,
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Sync successful:", result);

        // Clear plan cache and refresh
        planService.clearCache();
        const refreshedPlan = await planService.getCurrentUserPlan();
        console.log("✅ Refreshed plan:", refreshedPlan);

        // Reload page to reflect changes
        console.log("🔄 Reloading page...");
        window.location.reload();
      } else {
        const error = await response.text();
        console.error("❌ Sync failed:", response.status, error);
      }
    } catch (error) {
      console.error("❌ Sync error:", error);
    }
  };
  (window as any).clearLocalStorage = () => {
    console.log("🗑️ Clearing all localStorage data...");

    // List what we're about to clear
    const keys = Object.keys(localStorage);
    console.log("📋 Found localStorage keys:", keys);

    // Clear all localStorage
    localStorage.clear();

    console.log("✅ localStorage cleared successfully");
    console.log("🔄 Reloading page to reset application state...");

    // Reload the page
    window.location.reload();
  };
  (window as any).fixMyPlan = () => {
    const userData = localStorage.getItem("user_data");
    if (!userData) {
      console.error("❌ No user data found");
      return;
    }

    const user = JSON.parse(userData);
    if (user.email?.toLowerCase() === "mrkniazi@gmail.com") {
      console.log("🔧 Setting Basic plan for mrkniazi@gmail.com...");

      // Update localStorage directly
      user.subscription = { plan: "basic", status: "active" };
      localStorage.setItem("user_data", JSON.stringify(user));
      localStorage.setItem("user_plan", "basic");

      // Clear plan cache
      planService.clearCache();

      console.log("✅ Plan set to Basic. Reloading page...");
      window.location.reload();
    } else {
      console.log("❌ This fix is only for mrkniazi@gmail.com user");
    }
  };
  console.log("🔧 Plan Service available globally.");
  console.log("📱 Use forceRefreshPlan() to refresh plan data");
  console.log("🔍 Use debugPlanData() to see current data");
  console.log("🔄 Use syncCurrentUser() to sync subscription from Stripe");
  console.log("🗑️ Use clearLocalStorage() to clear all local data and reload");

  // Check if user needs syncing
  const userData = localStorage.getItem("user_data");
  if (userData) {
    const user = JSON.parse(userData);
    if (
      user.email === "mrkniazi@gmail.com" ||
      user.email?.toLowerCase() === "mrkniazi@gmail.com"
    ) {
      console.log(
        "🚨 DETECTED ISSUE: This user should have Basic Plan but may be showing Free Plan",
      );
      console.log(
        "💡 QUICK FIX: Run syncCurrentUser() in console to fix this issue",
      );
      console.log(
        "💡 IMMEDIATE FIX: Run fixMyPlan() to set Basic plan directly",
      );
    }
  }
}
